import type { HttpResponse } from '@capacitor/core';

type HeadersWithAccessors = HttpResponse['headers'] & Pick<Headers, 'get' | 'forEach'>;

/**
 * Adds the parts of the web `Headers` interface we use to a plain header object as returned
 * by Capacitors native HTTP, so both fetch flavours can be read the same way.
 *
 * `get` has to ignore case: the native layer hands back the casing the server sent
 * (eg `X-Captcha`), while callers look up the lowercase name.
 *
 * `forEach` is needed because graphql-request iterates the headers.
 * https://developer.mozilla.org/en-US/docs/Web/API/Headers/forEach
 */
export const addHeaderAccessors = (headers: HttpResponse['headers']) => {
  Object.defineProperty(headers, 'forEach', {
    value: (handler: (value: any, key: string) => any) => {
      return Object.keys(headers).forEach((key) => {
        return handler(headers[key], key);
      });
    },
    enumerable: false,
  });

  Object.defineProperty(headers, 'get', {
    value: (key: string) => {
      const name = Object.keys(headers).find(k => k.toLowerCase() === key.toLowerCase());

      return name === undefined ? null : headers[name];
    },
    enumerable: false,
  });

  return headers as HeadersWithAccessors;
};
